import { useMemo } from 'react';
import { EnvironmentalRiskCategory } from "src/enum";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import SolidGauge from 'highcharts/modules/solid-gauge';
import HighchartsMore from 'highcharts/highcharts-more';
// Initialize Highcharts More for gauge charts
HighchartsMore(Highcharts);
SolidGauge(Highcharts);
export const riskValueMapping: Record<EnvironmentalRiskCategory, number> = {
    [EnvironmentalRiskCategory.VERY_LOW]: 10,
    [EnvironmentalRiskCategory.LOW]: 25,
    [EnvironmentalRiskCategory.MEDIUM]: 50,
    [EnvironmentalRiskCategory.HIGH]: 65,
    [EnvironmentalRiskCategory.SEVERE]: 80,
    [EnvironmentalRiskCategory.EXTREME]: 100,
};

export const riskColorMapping: Record<EnvironmentalRiskCategory, string> = {
    [EnvironmentalRiskCategory.VERY_LOW]: '#4CAF50',   // Green
    [EnvironmentalRiskCategory.LOW]: '#8BC34A',        // Light Green
    [EnvironmentalRiskCategory.MEDIUM]: '#FFEB3B',     // Yellow
    [EnvironmentalRiskCategory.HIGH]: '#FF9800',       // Orange
    [EnvironmentalRiskCategory.SEVERE]: '#F44336',     // Dark Red
    [EnvironmentalRiskCategory.EXTREME]: '#B71C1C',    // Very Dark Red
};

type SolidGaugeChartProps = {
    title: string;
    category: EnvironmentalRiskCategory;
    riskLevelTitle?: string | null;
    onClick?: () => void;
    isClickable?: boolean;
    isUserRole?: boolean;
    isSampleProject?: boolean;
};

export const SolidGaugeChart = ({
    title,
    category,
    riskLevelTitle,
    onClick,
    isClickable = false,
    isUserRole = false,
    isSampleProject = false,
}: SolidGaugeChartProps) => {
    const value = riskValueMapping[category];
    const color = riskColorMapping[category];

    const options = useMemo(() => {
        return {
            chart: {
                type: 'solidgauge',
                height: 130,
                events: isClickable ? { click: () => onClick?.() } : undefined,
                style: { cursor: isClickable ? 'pointer' : 'default' },
            },
            title: {
                text: title,
                style: {
                    color: '#666666',
                    fontSize: '15px',
                    fontWeight: '600',
                },
                y: 15,
            },
            pane: {
                center: ['50%', '50%'],
                size: '100%',
                startAngle: -90,
                endAngle: 90,
                background: {
                    backgroundColor: '#EEE',
                    innerRadius: '70%',
                    outerRadius: '100%',
                    shape: 'arc',
                },
            },
            tooltip: { enabled: false },
            yAxis: {
                min: 0,
                max: 100,
                stops: [[1, color]], // solid color
                lineWidth: 0,
                tickWidth: 0,
                minorTickInterval: null,
                tickAmount: 0,
                title: {
                    text: riskLevelTitle ?? category,
                    align: 'middle',
                    style: {
                        color: '#666666',
                        fontSize: '12px',
                        fontWeight: '600',
                    },
                    y: 50,
                },
                labels: { enabled: false },
            },
            series: [
                {
                    name: 'Risk Level',
                    data: [value],
                    dataLabels: { enabled: false },
                },
            ],
            credits: { enabled: false },
            exporting: {
                enabled: false
            }
        };
    }, [category, color, isClickable, onClick, riskLevelTitle, title, value]);

    return <HighchartsReact
        highcharts={Highcharts}
        options={options}
        containerProps={{ style: { width: '100%' } }} // Ensures the chart's internal container respects the parent Box width
    />;
};
